import React, { useState } from 'react';
import { Network, ChevronDown, ChevronRight, Circle, Lock, AlertTriangle } from 'lucide-react';
import { Task, DependencyInfo } from '../shared/types';

interface DependencyTreeViewProps {
  tasks: Task[];
  getDependencyInfo: (task: Task) => Task[];
  getBlockedByDependencies: (task: Task) => DependencyInfo;
  getDelayTriggerCount: (task: Task) => number;
  getTotalCascadeImpact: (task: Task) => number;
}

export const DependencyTreeView: React.FC<DependencyTreeViewProps> = ({
  tasks,
  getDependencyInfo,
  getBlockedByDependencies,
  getDelayTriggerCount,
  getTotalCascadeImpact
}) => {
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});
  
  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <Network className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks to Display</h3>
        <p className="text-gray-600">No tasks match the current filters.</p>
      </div>
    );
  }
  
  // Build dependency tree - find root tasks (tasks with no dependencies)
  const rootTasks = tasks.filter(task => !task.dependencies || task.dependencies.length === 0);
  
  // Find tasks that depend on a given task
  const getSuccessors = (taskId: string) => {
    return tasks.filter(t => t.dependencies && t.dependencies.includes(taskId));
  };
  
  // Calculate maximum depth for proper width
  const getMaxDepth = (task: Task, depth = 0, visited: Set<string> = new Set()): number => {
    if (visited.has(task.id)) return depth;
    visited.add(task.id);
    
    const successors = getSuccessors(task.id);
    if (successors.length === 0) return depth;
    
    return Math.max(...successors.map(s => getMaxDepth(s, depth + 1, new Set(visited))));
  };
  
  const maxDepth = rootTasks.length > 0 
    ? Math.max(...rootTasks.map(task => getMaxDepth(task))) 
    : 0;
  const minContainerWidth = 600 + (maxDepth * 40) + 100; // card width + (levels * indent) + padding
  
  const toggleExpand = (taskId: string) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };
  
  // Get delay status color
  const getDelayStatus = (task: Task) => {
    if (task.status === 'Completed') return { color: 'text-gray-500', bg: 'bg-gray-50', label: 'Completed', icon: '' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let targetFinish: Date;
    if (task.weeklyRevision?.finishDate) {
      targetFinish = new Date(task.weeklyRevision.finishDate);
    } else if (task.monthlyRevision?.finishDate) {
      targetFinish = new Date(task.monthlyRevision.finishDate);
    } else {
      targetFinish = new Date(task.originalSchedule?.finishDate || task.finishDate || '');
    }
    targetFinish.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((today.getTime() - targetFinish.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays > 7) return { color: 'text-red-700', bg: 'bg-red-50', label: `${diffDays}d overdue` };
    if (diffDays > 0) return { color: 'text-orange-700', bg: 'bg-orange-50', label: `${diffDays}d overdue` };
    if (diffDays > -7) return { color: 'text-yellow-700', bg: 'bg-yellow-50', label: 'Due soon' };
    return { color: 'text-green-700', bg: 'bg-green-50', label: 'On track' };
  };
  
  // Render a task node
  const renderTaskNode = (task: Task, level = 0, visited: Set<string> = new Set()): React.ReactNode => {
    // Prevent infinite loops in case of circular dependencies
    if (visited.has(task.id)) {
      return (
        <div key={`${task.id}-circular`} style={{ marginLeft: `${level * 40}px` }} className="mb-2">
          <div className="flex items-center p-3 bg-red-50 border-l-4 border-red-500 rounded">
            <span className="text-red-700 text-sm inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Circular dependency detected</span>
          </div>
        </div>
      );
    }
    
    visited.add(task.id);
    
    const successors = getSuccessors(task.id);
    const hasSuccessors = successors.length > 0;
    const isExpanded = expandedTasks[task.id];
    const blockInfo = getBlockedByDependencies(task);
    const triggerCount = getDelayTriggerCount(task);
    const totalImpact = getTotalCascadeImpact(task);
    const delayStatus = getDelayStatus(task);
    
    const cardWidth = 600; // Base card width
    const indent = level * 40; // Indentation per level
    
    return (
      <div key={task.id} className="mb-2">
        {/* Task Card */}
        <div 
          className={`flex items-center p-3 border-l-4 rounded shadow-sm ${delayStatus.bg} ${
            triggerCount > 0 ? 'border-orange-500' : blockInfo.isBlocked ? 'border-red-500' : 'border-blue-500'
          }`} 
          style={{ 
            marginLeft: `${indent}px`,
            minWidth: `${cardWidth}px`,
            width: 'fit-content'
          }}
        >
          {/* Expand/Collapse Button */}
          {hasSuccessors && (
            <button
              onClick={() => toggleExpand(task.id)}
              className="mr-2 p-1 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          {!hasSuccessors && <div className="w-7 mr-2"></div>}
          
          {/* Task Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{task.taskName}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${delayStatus.color} ${delayStatus.bg} border border-current`}>
                {delayStatus.label}
              </span>
              {blockInfo.isBlocked && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 border border-red-300">
                  <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Blocked by {blockInfo.blockedBy.length}</span>
                </span>
              )}
              {totalImpact > 0 && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-600 text-white font-bold border border-red-800">
                  IMPACT: {totalImpact} tasks
                </span>
              )}
              {triggerCount > 0 && totalImpact > triggerCount && (
                <span className="px-2 py-0.5 rounded text-xs bg-orange-50 text-orange-600 italic">
                  ({triggerCount} direct + {totalImpact - triggerCount} cascade)
                </span>
              )}
              {hasSuccessors && (
                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                  {successors.length} successor{successors.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {task.building} • {task.floor} • {task.category} • {task.status}
            </div>
          </div>
        </div>
        
        {/* Render Successors */}
        {hasSuccessors && isExpanded && (
          <div className="mt-2">
            {successors.map(successor => renderTaskNode(successor, level + 1, new Set(visited)))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="p-4">
      <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
        <div className="flex items-center gap-2 text-sm">
          <Network className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-blue-900">Dependency Tree View</span>
          <span className="text-blue-700">• Click chevron to expand/collapse successors • Root tasks have no predecessors</span>
        </div>
      </div>
      
      <div className="space-y-2 pb-4" style={{ minWidth: `${minContainerWidth}px` }}>
        {rootTasks.length > 0 ? (
          rootTasks.map(task => renderTaskNode(task))
        ) : (
          <div className="text-center py-8 text-gray-500">
            All tasks have dependencies. No root tasks found.
            <div className="text-xs mt-2">This might indicate circular dependencies or all tasks are waiting for predecessors.</div>
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-sm font-semibold text-gray-700 mb-2">Legend:</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-green-500 text-green-500" />
            <span>On track (&gt;7 days ahead)</span>
          </div>
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span>Due soon (&lt;7 days)</span>
          </div>
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-orange-500 text-orange-500" />
            <span>Overdue (1-7 days)</span>
          </div>
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-red-500 text-red-500" />
            <span>Critical (&gt;7 days overdue)</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-3 h-3" />
            <span>Blocked by delayed predecessors</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" />
            <span>Delay trigger (blocking others)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

